import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import {
  DELETE_CONFIRMATION_CONSTANTS,
  REQUEST_METHODS,
} from "@/constants/global-constants";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type UseDocumentManagerProps = {
  type: string; // Determines which API to use
};

export const useDocumentManager = ({
  type,
}: UseDocumentManagerProps): {
  handleLoadSample: (id: string) => Promise<void>;
  handleDeleteAll: (id: string) => Promise<void>;
  handleDelete: (id: string, entryType: string, typeId: string) => void;
  handleRename: (
    id: string,
    newName: string,
    itemType?: string,
  ) => Promise<void>;
} => {
  const dispatch = useDispatch();
  const [alterLeadsDrive] = useAlterLeadsDriveMutation();
  const [alterProject] = useAlterProjectMutation();
  /**
   * Handles actual loading of sample directory after confirmation
   */
  const getIdKey = useCallback(() => {
    return type === driveTypes.PROJECT_REGISTER ? "leadsId" : "projectId";
  }, [type]);

  const getMutationHandler = useCallback(() => {
    return type === driveTypes.PROJECT_REGISTER
      ? alterLeadsDrive
      : alterProject;
  }, [type, alterProject, alterLeadsDrive]);

  const getApiPath = useCallback(() => {
    return type === driveTypes.PROJECT_REGISTER ? "drive/" : "document/";
  }, [type]);

  const handleLoadSample = useCallback(
    (id: string): Promise<void> => {
      const mutationHandler = getMutationHandler();
      const idKey = getIdKey();
      const apiPath = getApiPath();

      return new Promise((resolve) => {
        dispatch(
          showAlertDialog({
            title: "Load Sample Directory",
            desc: DRIVE_CONSTANTS.MESSAGES.LOAD_SAMPLE_WARNING,
            btnName: "Load",
            cancelName: "Cancel",
            btnAction: async (): Promise<void> => {
              try {
                const res = await mutationHandler({
                  [idKey]: id,
                  method: "GET",
                  params: `${apiPath}load-sample`,
                });

                validateResponse({
                  response: res,
                  onSuccess: () => {
                    customToast.success(DRIVE_CONSTANTS.MESSAGES.SAMPLE_LOADED);
                  },
                  onError: () => {
                    customToast.error(DRIVE_CONSTANTS.MESSAGES.SAMPLE_FAILED);
                  },
                });
              } finally {
                resolve();
              }
            },
          }),
        );
      });
    },
    [dispatch, getMutationHandler, getIdKey, getApiPath],
  );

  const handleDeleteAll = useCallback(
    (id: string): Promise<void> => {
      const mutationHandler = getMutationHandler();
      const idKey = getIdKey();

      return new Promise((resolve) => {
        dispatch(
          showAlertDialog({
            title: DRIVE_CONSTANTS.LABELS.DELETE_ALL,
            desc: DRIVE_CONSTANTS.MESSAGES.DELETE_ALL_WARNING,
            btnName: "Delete All",
            cancelName: "Cancel",
            btnAction: async (): Promise<void> => {
              try {
                const res = await mutationHandler({
                  [idKey]: id,
                  method: REQUEST_METHODS.DELETE,
                  params: `drive/dir/ROOT/delete/children/driveType/${type}`,
                });

                validateResponse({
                  response: res,
                  onSuccess: () => {
                    customToast.success(
                      DRIVE_CONSTANTS.MESSAGES.DELETE_ALL_SUCCESS,
                    );
                  },
                  onError: () => {
                    customToast.error(
                      DRIVE_CONSTANTS.MESSAGES.DELETE_ALL_FAILED,
                    );
                  },
                });
              } finally {
                resolve();
              }

              return;
            },
          }),
        );
      });
    },
    [getMutationHandler, getIdKey, dispatch, type],
  );

  /**
   * Handles file/folder deletion
   */
  const handleDelete = useCallback(
    (id: string, entryType: string, typeId: string): void => {
      const getParams = (): string => {
        if (entryType === "document") {
          return `drive/${id}/driveType/${type}`;
        }

        // include driveType in params for folders
        return `drive/dir/${id}/delete/children/driveType/${type}`;
      };
      const mutationHandler = (():
        | typeof alterProject
        | typeof alterLeadsDrive => {
        if (
          type === driveTypes.PROJECT ||
          type === driveTypes.TASK_AND_CLAIMS
        ) {
          return alterProject;
        } else {
          return alterLeadsDrive;
        }
      })();

      dispatch(
        showAlertDialog({
          title: DELETE_CONFIRMATION_CONSTANTS.title,
          desc: DRIVE_CONSTANTS.MESSAGES.DELETE_DOC_WARNING,
          btnName: DELETE_CONFIRMATION_CONSTANTS.btnName,
          cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
          btnAction: async (): Promise<void> => {
            try {
              const res = await mutationHandler({
                [type === driveTypes.PROJECT_REGISTER
                  ? "leadsId"
                  : "projectId"]: typeId,
                method: REQUEST_METHODS.DELETE,
                params: getParams(),
              });

              validateResponse({
                response: res,
                onSuccess: () => {
                  customToast.success(
                    entryType === "document"
                      ? DRIVE_CONSTANTS.MESSAGES.DOCUMENT_DELETED
                      : DRIVE_CONSTANTS.MESSAGES.DIRECTORY_DELETED,
                  );
                },
              });
            } catch (error) {
              customToast.error("An error occurred while deleting the item");
            }
          },
        }),
      );
    },
    [type, alterProject, alterLeadsDrive, dispatch],
  );

  /**
   * Handles directory/document renaming
   */
  const handleRename = useCallback(
    (id: string, newName: string, itemType: string = "dir"): Promise<void> => {
      const mutationHandler = getMutationHandler();
      const idKey = getIdKey();
      const apiPath = getApiPath();

      // Determine the correct API endpoint based on item type
      const endpoint =
        itemType === "dir"
          ? `${apiPath}dir/${id}/rename`
          : `${apiPath}${id}/rename`;

      return new Promise((resolve) => {
        dispatch(
          showAlertDialog({
            title: `Rename ${itemType === "dir" ? "Directory" : "Document"}`,
            desc: `Are you sure you want to rename this ${itemType === "dir" ? "directory" : "document"}?`,
            btnName: "Rename",
            cancelName: "Cancel",
            btnAction: async (): Promise<void> => {
              try {
                const res = await mutationHandler({
                  [idKey]: id,
                  method: REQUEST_METHODS.PUT,
                  params: endpoint,
                  data: { name: newName, driveType: type },
                });

                validateResponse({
                  response: res,
                  onSuccess: () => {
                    customToast.success(
                      itemType === "dir"
                        ? DRIVE_CONSTANTS.MESSAGES.DIRECTORY_RENAMED
                        : DRIVE_CONSTANTS.MESSAGES.DOCUMENT_RENAMED,
                    );
                  },
                });
              } finally {
                resolve();
              }
            },
          }),
        );
      });
    },
    [dispatch, getMutationHandler, getIdKey, getApiPath, type],
  );

  return {
    handleLoadSample,
    handleDeleteAll,
    handleDelete,
    handleRename,
  };
};
